/**
 * Business Intelligence — people asking Orchid for an Agent profile.
 *
 * The founder, 16 September 2026:
 *
 *   "there are two ways to add a property: 'I own it' and 'I manage it.' Let
 *    there be a prompt when one chooses the Manage option: landlord profiles
 *    can only add units they own. Please request for Orchid to add an Agent
 *    profile in order to add units that you manage. --> and then, there must be
 *    a button to 'Send Request' and this request must reach a desk in the
 *    Portal. Then we can gather stats about how many people want to add units
 *    they don't own but manage, and we'll have an idea about how to build the
 *    Agency profile."
 *
 * This is that desk, and the figures are the point of it.
 *
 * ---------------------------------------------------------------------------
 * THE TWO NUMBERS THAT ARE NOT THE SAME NUMBER, AND WHY BOTH ARE SHOWN
 *
 * PEOPLE is how many want this. It is one row per person in the platform, not
 * one per press, enforced by a UNIQUE rather than trusted to a screen — a table
 * counting presses would answer a different question while looking like it
 * answered his.
 *
 * UNITS is the number that decides what an Agency profile has to be: forty
 * people managing two each and four managing four hundred are the same
 * headcount and a completely different product.
 *
 * AND THE UNIT FIGURE IS OPTIONAL, SO HOW MANY ANSWERED IS PRINTED BESIDE IT.
 * A total built from a third of the replies, sitting next to a headline count of
 * everybody, is a statistic that misleads the person who commissioned it. That
 * is the single most important line on this screen.
 *
 * ---------------------------------------------------------------------------
 * NO NAMES, AND NO REPLY.
 *
 * `ofx_connect_code` identifies the account without naming anybody —
 * Prohibition 6, the same choice every other queue in this folder makes.
 *
 * And there is no way to answer these people from here, because none is built.
 * The screen says so rather than implying a response is on its way.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, Notice, QuietButton, Spinner,
} from '../ui';
import {
  fetchAgentProfileInterest, fetchAgentProfileDemand, markAgentProfileRequestSeen,
} from '../lib/agentProfiles';

const Figure = ({ label, value, note }) => (
  <div style={{ flex: '1 1 120px', minWidth: 0 }}>
    <p style={{
      fontFamily: SANS, fontSize: 22, fontWeight: 700, color: O.ink,
      margin: '0 0 2px', fontVariantNumeric: 'tabular-nums',
    }}>
      {value}
    </p>
    <p style={{
      fontFamily: SANS, fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase',
      color: O.inkFaint, margin: 0,
    }}>
      {label}
    </p>
    {note && (
      <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, margin: '4px 0 0' }}>
        {note}
      </p>
    )}
  </div>
);

const Row = ({ ask, onSeen, busy }) => {
  const [reason, setReason] = useState('');
  const seen = Boolean(ask.seen_at);

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
        marginBottom: 8,
      }}>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <p style={{
            fontFamily: SANS, fontSize: 14, fontWeight: 700, color: O.ink,
            margin: '0 0 2px',
          }}>
            {ask.ofx_connect_code || '— no OFX code on this account yet'}
          </p>
          <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkFaint, margin: 0 }}>
            {ask.units_managed == null
              // NOT "0". They did not say, and a nought here would be counted by
              // a reader as "manages nothing", which is the opposite.
              ? 'Did not say how many units they manage'
              : `Manages ${ask.units_managed} unit${ask.units_managed === 1 ? '' : 's'}`}
            {ask.units_on_orchid > 0 && ` · ${ask.units_on_orchid} already on Orchid`}
            {ask.cities?.length > 0 && ` · ${ask.cities.join(', ')}`}
          </p>
        </div>
        <StatusPill tone={seen ? 'good' : 'quiet'}>
          {seen ? 'Read' : 'New'}
        </StatusPill>
      </div>

      {ask.note && (
        <p style={{
          fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted,
          margin: '0 0 8px', maxWidth: '70ch',
        }}>
          “{ask.note}”
        </p>
      )}

      {ask.times_asked > 1 && (
        <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, margin: '0 0 8px' }}>
          {/* ONE PERSON, SEVERAL PRESSES. Worth showing because insistence is a
              signal, and worth wording carefully because it is NOT a second
              person and must never be read as one. */}
          Asked {ask.times_asked} times.
        </p>
      )}

      {!seen && (
        <>
          <label style={{ display: 'block', marginBottom: 10 }}>
            <span style={{
              fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, display: 'block',
              marginBottom: 4,
            }}>
              Why you are opening this — it is kept on the record with your name
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              aria-label="Why you are opening this — it is kept on the record with your name"
              style={{
                width: '100%', boxSizing: 'border-box', fontFamily: SANS, fontSize: 13,
                padding: '9px 11px', borderRadius: 9, border: `1px solid ${O.hairline}`,
                resize: 'vertical', color: O.ink, background: O.card,
              }}
            />
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <QuietButton
              style={{ flex: 1 }}
              disabled={busy}
              onClick={() => onSeen(ask.request_id, reason)}
            >
              Mark it read
            </QuietButton>
          </div>
        </>
      )}
    </Card>
  );
};

export default function AgentProfileInterest() {
  const [state, setState] = useState({ loading: true, error: null, rows: [], demand: null });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    const [list, demand] = await Promise.all([
      fetchAgentProfileInterest(), fetchAgentProfileDemand(),
    ]);
    if (list.error) { setState({ loading: false, error: list.error, rows: [], demand: null }); return; }
    setState({
      loading: false, error: null, rows: list.data, demand: demand.data || null,
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const seen = async (requestId, reason) => {
    setBusy(true);
    setNote(null);
    const { error } = await markAgentProfileRequestSeen(requestId, reason);
    setBusy(false);
    if (error) { setNote({ tone: 'critical', text: error }); return; }
    await load();
  };

  if (state.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0' }}>
        <Spinner size={16} />
        <span style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted }}>
          Reading the list…
        </span>
      </div>
    );
  }

  if (state.error) return <Notice tone="critical">{state.error}</Notice>;

  const d = state.demand;

  return (
    <div>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted,
        margin: '0 0 14px', maxWidth: '74ch',
      }}>
        A landlord profile can only add units the person owns. Everybody here
        chose “I manage it” and asked Orchid to open an Agent profile instead.
        Orchid cannot answer them from this screen — there is no reply path built.
      </p>

      {d && (
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <Figure label="People" value={d.people}
                    note={d.unseen > 0 ? `${d.unseen} not yet read` : 'all read'} />
            <Figure
              label="Units they manage"
              value={d.unitsManagedTotal}
              /* THE MOST IMPORTANT LINE ON THIS SCREEN. Without it the total
                 reads as though every one of them had answered. */
              note={`from the ${d.answered} of ${d.people} who said`}
            />
            <Figure label="Largest one" value={d.biggest}
                    note={d.biggest === 0 ? 'nobody gave a figure' : 'units, one person'} />
          </div>
        </Card>
      )}

      {note && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone={note.tone}>{note.text}</Notice>
        </div>
      )}

      {state.rows.length === 0 ? (
        /* NOBODY HAS ASKED IS AN ANSWER, and for this desk it is the whole
           point: it is the demand figure reading zero, not a screen that failed
           to load. MDS 2.2 -- do not fill it. */
        <Notice tone="info">
          Nobody has asked for an Agent profile yet.
        </Notice>
      ) : (
        state.rows.map((a) => (
          <Row key={a.request_id} ask={a} onSeen={seen} busy={busy} />
        ))
      )}
    </div>
  );
}
